import { useLayoutEffect } from "react";
import { setDocumentLanguage, translateText } from "./i18n";

const originalText = new WeakMap();
const originalAttributes = new WeakMap();
const TRANSLATABLE_ATTRIBUTES = [
  "placeholder",
  "title",
  "aria-label",
  "alt",
  "data-tooltip",
];
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "CODE", "PRE", "TEXTAREA"]);

export function getOriginalElementAttribute(element, name) {
  if (!(element instanceof Element)) return "";
  const stored = originalAttributes.get(element);
  return stored && Object.prototype.hasOwnProperty.call(stored, name)
    ? stored[name]
    : element.getAttribute(name) || "";
}

export function getOriginalElementText(element) {
  if (!(element instanceof Element)) return "";
  const parts = [];
  const collect = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      parts.push(
        originalText.has(node) ? originalText.get(node) : node.nodeValue || "",
      );
      return;
    }
    if (node.nodeType === Node.ELEMENT_NODE)
      Array.from(node.childNodes).forEach(collect);
  };
  Array.from(element.childNodes).forEach(collect);
  return parts.join(" ").replace(/\s+/g, " ").trim();
}

function translateTextNode(node, language) {
  const parent = node.parentElement;
  if (
    !parent ||
    SKIP_TAGS.has(parent.tagName) ||
    parent.closest('[data-no-translate="true"]')
  )
    return;
  if (!originalText.has(node)) originalText.set(node, node.nodeValue || "");
  const source = originalText.get(node);
  const next = language === "hi" ? translateText(source, language) : source;
  if (node.nodeValue !== next) node.nodeValue = next;
}

function translateElement(element, language) {
  if (
    !(element instanceof Element) ||
    element.closest('[data-no-translate="true"]')
  )
    return;
  let stored = originalAttributes.get(element);
  if (!stored) {
    stored = {};
    originalAttributes.set(element, stored);
  }
  TRANSLATABLE_ATTRIBUTES.forEach((name) => {
    if (!element.hasAttribute(name)) return;
    if (!(name in stored)) stored[name] = element.getAttribute(name) || "";
    const source = stored[name];
    const next = language === "hi" ? translateText(source, language) : source;
    if (element.getAttribute(name) !== next) element.setAttribute(name, next);
  });
  Array.from(element.childNodes).forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) translateTextNode(child, language);
    else if (child.nodeType === Node.ELEMENT_NODE)
      translateElement(child, language);
  });
}

export default function LanguageRuntime({ language }) {
  useLayoutEffect(() => {
    const normalized = setDocumentLanguage(language);
    let translating = false;
    const apply = (root = document.body) => {
      if (!root || translating) return;
      translating = true;
      try {
        if (root.nodeType === Node.TEXT_NODE)
          translateTextNode(root, normalized);
        else if (root.nodeType === Node.ELEMENT_NODE)
          translateElement(root, normalized);
      } finally {
        translating = false;
      }
    };
    apply();
    const originalTitle = document.title;
    document.title =
      normalized === "hi"
        ? translateText(originalTitle, normalized)
        : originalTitle;
    const observer = new MutationObserver((mutations) => {
      if (translating) return;
      mutations.forEach((mutation) => {
        if (mutation.type === "characterData") {
          const current = mutation.target.nodeValue || "";
          const previousSource = originalText.get(mutation.target);
          const expected =
            previousSource == null
              ? null
              : translateText(previousSource, normalized);
          if (current !== expected) originalText.set(mutation.target, current);
          apply(mutation.target);
        } else if (mutation.type === "attributes") {
          const stored = originalAttributes.get(mutation.target) || {};
          const current =
            mutation.target.getAttribute(mutation.attributeName) || "";
          const expected =
            stored[mutation.attributeName] == null
              ? null
              : translateText(stored[mutation.attributeName], normalized);
          if (current !== expected) stored[mutation.attributeName] = current;
          originalAttributes.set(mutation.target, stored);
          apply(mutation.target);
        } else mutation.addedNodes.forEach(apply);
      });
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: TRANSLATABLE_ATTRIBUTES,
    });
    return () => observer.disconnect();
  }, [language]);
  return null;
}
